import React from "react";
import { useState, useEffect, createRef } from "react";

import "./SpotifyAudioPlayer.css";

/**
 * @param {Object} props
 * @param {import("../../lib/DataModels/AudioSimilarityTaskInput").SpotifyAudioPlayerRecord} props.record record to play
 * @param {Boolean=} props.disabledPlayButton whether play button should be disabled. default false
 * @param {() => {}=} props.onPlaybackStarted Triggered on manual start of playback
 * @param {() => {}=} props.onPlayBackStopped Triggered on manual stop of playback
 * @param {() => {}=} props.onRecordFullyPlayed Triggered on playback stopped automatically because the record was played to the end
 */
const SpotifyAudioPlayer = ({
    record,
    disabledPlayButton = false,
    onPlaybackStarted = () => {},
    onPlayBackStopped = () => {},
    onRecordFullyPlayed = () => {},
    ...rest
}) => {
    const [isPlaybackStarted, setPlaybackStarted] = useState(false);
    const [currentProgress, setCurrentProgress] = useState("00:00");
    const audioPlayerRef = createRef();

    useEffect(() => {
        if (record !== undefined && record !== null) {
            audioPlayerRef.current.currentTime = record.start;
        }
        // ensures that switching the record always has playback not started
        if (isPlaybackStarted) {
            setPlaybackStarted(false);
        }
        // eslint-disable-next-line
    }, [record]);

    if (record === undefined || record === null) {
        return <></>;
    }

    const formatProgressTime = secs => {
        const sec_num = parseInt(secs, 10);
        const minutes = Math.floor(sec_num / 60) % 60;
        const seconds = sec_num % 60;
        return [minutes, seconds].map(v => (v < 10 ? "0" + v : v)).join(":");
    };

    const startAudioPlayer = () => {
        setPlaybackStarted(true);
        audioPlayerRef.current.play();
        onPlaybackStarted();
    };

    const stopAudioPlayer = () => {
        setPlaybackStarted(false);
        audioPlayerRef.current.pause();
        audioPlayerRef.current.currentTime = record.start;
    };

    const playIcon = (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 110 110"
            width="150px"
        >
            <g>
                <g>
                    <g
                        fill="none"
                        stroke={disabledPlayButton ? "#444" : "#f4f4f4"}
                        data-name="button-border"
                    >
                        <circle cx="55" cy="55" r="55" stroke="none" />
                        <circle cx="55" cy="55" r="54.5" />
                    </g>
                    <g
                        fill={disabledPlayButton ? "#444" : "#f4f4f4"}
                        data-name="play-button"
                    >
                        <path d="M48.13164464 66.50112144V41.67511658l20.82482064 12.41300285-20.82482064 12.41300201z" />
                        <path d="M67.97991696 54.08811943L48.63164464 42.55524055v23.06575776l19.34827232-11.53287888m1.95308685 0L47.63164464 67.38123886V40.795L69.9330038 54.08811943z" />
                    </g>
                </g>
            </g>
        </svg>
    );

    const pauseIcon = (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 110 110"
            width="150px"
        >
            <g>
                <g fill="none" stroke="#f4f4f4">
                    <g data-name="button-border">
                        <circle cx="55" cy="55" r="55" stroke="none" />
                        <circle cx="55" cy="55" r="54.5" />
                    </g>
                    <g
                        strokeLinecap="round"
                        strokeWidth="2.5"
                        data-name="play-button"
                    >
                        <path d="M48.105 41.495v25.886" />
                        <path d="M62.105 41.495v25.886" />
                    </g>
                </g>
            </g>
        </svg>
    );

    const playButtonIcon = isPlaybackStarted ? pauseIcon : playIcon;
    return (
        <>
            <div {...rest} className="spotify_audioplayer_container">
                <div className="spotify_audioplayer_progress">
                    <span className="spotify_audioplayer_progress_current">
                        {currentProgress}
                    </span>
                    <span> - </span>
                    <span className="spotify_audioplayer_progress_duration">
                        {formatProgressTime(record.end - record.start)}
                    </span>
                </div>
                <button
                    className="spotify_audioplayer_play_button"
                    disabled={disabledPlayButton}
                    style={{ outline: "none" }}
                    onClick={() => {
                        if (isPlaybackStarted) {
                            stopAudioPlayer();
                            onPlayBackStopped();
                        } else {
                            // it is intended that every start plays from the beginning
                            startAudioPlayer();
                        }
                    }}
                >
                    {playButtonIcon}
                </button>
                <a
                    href={"https://open.spotify.com/track/" + record.record_id}
                    className="spotify_audioplayer_spotify_link"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 755.90668 226.72"
                        style={{
                            maxWidth: "120px",
                            width: "auto",
                            display: "inline",
                            float: "right"
                        }}
                    >
                        <g fill="white">
                            <path d="M183.37599542 100.68880315c-35.9919991-21.3741328-95.35986429-23.33946608-129.71866343-12.91146634-5.5177332 1.67333329-11.35213305-1.4413333-13.02399967-6.9586665-1.67186663-5.51999986 1.43959996-11.35066638 6.96093316-13.02799967 39.44173234-11.97333303 105.00706404-9.65999976 146.44172967 14.93599963 4.96399988 2.94533326 6.59199983 9.35466643 3.64933324 14.31053297-2.94399993 4.96199988-9.35866643 6.5978665-14.30933297 3.65159991zm-1.17866664 31.6587992c-2.52399994 4.09786657-7.88133314 5.3817332-11.97333304 2.86719994-30.00666591-18.44479954-75.76306477-23.7879994-111.26306388-13.01253301-4.60359989 1.39119996-9.4661331-1.2041333-10.86399973-5.79946652-1.38759996-4.60359989 1.2093333-9.4573331 5.80466652-10.85733306 40.55413232-12.30546636 90.96773106-6.34519985 125.4317302 14.83439963 4.0919999 2.5198666 5.37999987 7.88026646 2.86399993 11.96773303zm-13.66266633 30.40413258c-2.00533328 3.28959992-6.2906665 4.32133323-9.56799976 2.31613328-26.22079934-16.02493293-59.22346518-19.64519951-98.09013088-10.7666664-3.74586657.85893331-7.47813314-1.48906663-8.33279979-5.2333332-.85773331-3.7451999 1.48026663-7.47706648 5.23439987-8.33173313 42.53279894-9.72346642 79.01786469-5.5385332 108.44853062 12.44533303 3.28133325 2.00359995 4.31466656 6.2906665 2.30799994 9.57026642zM117.32759707 1.7026723c-61.66559846 0-111.65781055 49.98933209-111.65781055 111.65533054 0 61.6713318 49.99221209 111.65939721 111.65781055 111.65939721 61.66839846 0 111.65773054-49.98806541 111.65773054-111.6593972 0-61.66599846-49.98933208-111.65533055-111.65773054-111.65533055M309.45199226 104.78146972c-19.27866618-4.59746656-22.71199943-7.82453314-22.71199943-14.60279964 0-6.4066665 6.03199985-10.71599973 14.99999963-10.71599973 8.69333311 0 17.31199956 3.27333325 26.350666 10.01199975.27333333.204.61599999.28533332.95333331.232.33733333-.05066667.63466665-.23466667.83333332-.51466666l9.4133331-13.269333c.38666665-.54533332.28133332-1.29999997-.24-1.7173333-10.75599973-8.63066644-22.86799943-12.82666634-37.0253324-12.82666634-20.81599949 0-35.35599912 12.49066635-35.35599912 30.36533257 0 19.16719953 12.54266635 25.95306602 34.21733247 31.19159922 18.44799954 4.24946657 21.5613328 7.80893314 21.5613328 14.17399965 0 7.05159982-6.29599984 11.43586638-16.4279996 11.43586638-11.2519997 0-20.43199948-3.79053324-30.69999922-12.68279968-.25466666-.21866666-.60399999-.31973333-.92666665-.30146666-.33866665.02813333-.64933331.18533333-.8653333.44519999l-10.55466641 12.56146635c-.44266666.52146665-.38666666 1.29999997.124 1.75053329 11.9479997 10.66559973 26.63999933 16.2999996 42.49599893 16.2999996 22.4306661 0 36.92533241-12.2571997 36.92533241-31.22653256 0-16.03173293-9.57866642-24.89786604-33.06666584-30.61039923M409.5493231 126.26786918c0 13.53759966-8.33866646 22.98386609-20.27866617 22.98386609-11.8039997 0-20.70799948-9.87546642-20.70799948-22.9838661 0-13.10626633 8.90399978-22.98333275 20.70799948-22.98333275 11.74799971 0 20.27866616 9.66359976 20.27866616 22.98333276zm-16.28266626-40.49986566c-9.72266643 0-17.6973329 3.82933324-24.27333273 11.67599971v-8.83199978c0-.69733331-.56533332-1.2653333-1.26266664-1.2653333h-17.2613329c-.69733331 0-1.2613333.56799999-1.2613333 1.2653333v98.13613088c0 .69733332.56399999 1.26506664 1.2613333 1.26506664h17.2613329c.69733332 0 1.26266664-.56773332 1.26266664-1.26506664v-30.97599922c6.57733317 7.38119981 14.55333297 10.98639972 24.27333273 10.98639972 18.06533288 0 36.35333242-13.90679965 36.35333242-40.49066565 0-26.58959934-18.28799954-40.49986566-36.35333242-40.49986566M476.49732142 149.3934686c-12.37466636 0-21.70399946-9.94279975-21.70399946-23.12559942 0-13.23799967 9.00666644-22.84479943 21.41866613-22.84479943 12.45466636 0 21.84666613 9.94226642 21.84666613 23.13386609 0 13.236533-9.06799978 22.83653276-21.5613328 22.83653276zm0-63.62546508c-23.26533275 0-41.49199896 17.91506622-41.49199896 40.78893232 0 22.62559943 18.10133288 40.35266566 41.20666563 40.35266566 23.34799942 0 41.63066563-17.85466623 41.63066563-40.64173232 0-22.71039943-18.15733288-40.49986566-41.3453323-40.49986566M567.52531915 87.34667015h-18.99599953V67.9253373c0-.69733331-.56266665-1.26399997-1.25999997-1.26399997h-17.25999957c-.69866665 0-1.26666663.56666666-1.26666663 1.26399997v19.42133285h-8.2999998c-.69599998 0-1.2573333.56799999-1.2573333 1.2653333v14.83559963c0 .69693332.56133332 1.2645333 1.2573333 1.2645333h8.2999998v38.38906571c0 15.51359961 7.72133314 23.37866608 22.94933276 23.37866608 6.19199984 0 11.32933305-1.27866663 16.17066626-4.02386656.39333332-.21986666.63866665-.64386665.63866665-1.09479998V147.233602c0-.43653333-.228-.84693332-.59999998-1.0761333-.376-.23586667-.84399998-.24786667-1.2293333-.0572-3.32533326 1.67279995-6.53999984 2.44519993-10.13333309 2.44519993-5.5373332 0-8.00933313-2.5134666-8.00933313-8.14946646v-35.68386578h18.99599953c.69733331 0 1.25866663-.56759998 1.25866663-1.2645333V88.61200345c0-.69733331-.56133332-1.2653333-1.25866663-1.2653333M633.70798416 87.42267015v-2.38533327c0-7.01733316 2.6906666-10.14666642 8.72533311-10.14666642 3.59866658 0 6.48933317.71466665 9.72666643 1.79466662.39866665.12533333.81466664.06266667 1.13866663-.176.33333333-.23866665.52133332-.6213333.52133332-1.02666663v-14.5466663c0-.556-.35733332-1.04799998-.89199997-1.21066664-3.41999992-1.01733331-7.7959998-2.06266662-14.34799964-2.06266662-15.94533294 0-24.3719994 8.97866645-24.3719994 25.95599935v3.65333325h-8.29333312c-.69599998 0-1.26666664.56666665-1.26666664 1.26399996v14.91159963c0 .69693332.57066666 1.2645333 1.26666664 1.2645333h8.29333312v59.21306519c0 .69746665.56266666 1.2641333 1.25866664 1.2641333h17.2613329c.69733332 0 1.2653333-.56666665 1.2653333-1.2641333v-59.21306519h16.11733293l24.68933272 59.19746519c-2.8026666 6.21973318-5.55866653 7.45733315-9.3213331 7.45733315-3.04133326 0-6.24399984-.90839998-9.51866643-2.69999994-.308-.16986666-.67199998-.19746666-1.0053333-.0948-.32933333.1172-.60666666.36146666-.74666666.68279999l-5.85066652 12.83586635c-.27866666.60626665-.04133333 1.31879996.54266666 1.63333329 6.10799984 3.30786658 11.62266637 4.71986655 18.43599954 4.71986655 12.74666634 0 19.7919995-5.93853319 26.00399935-21.91199946l29.94799925-77.38666473c.14933333-.38933332.10533333-.82799998-.13333333-1.17466663-.23733333-.34133333-.61999999-.548-1.03866664-.548h-17.97066622c-.53733332 0-1.01999997.34133333-1.19599997.84666665l-18.40933287 52.58399869-20.1639995-52.61866535c-.184-.48933332-.65466665-.81199998-1.17866664-.81199998h-29.4893326M595.33998512 87.34667015h-17.26266624c-.69733331 0-1.26399997.56799999-1.26399997 1.2653333v75.31319812c0 .69746665.56666666 1.2641333 1.26399997 1.2641333h17.26266624c.69599998 0 1.26399996-.56666665 1.26399996-1.2641333V88.61200345c0-.69733331-.56799998-1.2653333-1.26399996-1.2653333M586.79598533 53.054671c-6.83733316 0-12.38799969 5.5373332-12.38799969 12.37466637 0 6.8426665 5.55066653 12.38533302 12.38799969 12.38533302 6.83599983 0 12.3799997-5.54266653 12.3799997-12.38533302 0-6.83733317-5.54399987-12.37466636-12.3799997-12.37466636M738.73198153 94.83826996h-3.16266659v4.03333324h3.1626666c1.57866662 0 2.52133326-.77239998 2.52133326-2.01773329 0-1.31199996-.94266664-2.01559995-2.52133327-2.01559995zm2.05066662 5.75359986l3.43599991 4.81146655h-2.89733326l-3.09333326-4.41306656h-2.6586666v4.41306656h-2.42533327V92.65067002h5.68666653c2.96133326 0 4.91066654 1.51626663 4.91066654 4.06773323 0 2.09013328-1.20799997 3.36773325-2.9586666 3.87346657zm-2.69333327-12.18386636c-6.22266651 0-10.93199973 4.9453332-10.93199973 11.00359972 0 6.05426652 4.67599989 10.93439973 10.86799973 10.93439973 6.22133318 0 10.9346664-4.94266654 10.9346664-11.00359973 0-6.05439984-4.67866655-10.93439972-10.8706664-10.93439972zm-.064 23.15319942c-6.83199983 0-12.1479997-5.48706653-12.1479997-12.1495997 0-6.66359983 5.3853332-12.21293303 12.2119997-12.21293303 6.8306665 0 12.1479997 5.48666653 12.1479997 12.14373303 0 6.66359984-5.3813332 12.2187997-12.2119997 12.2187997" />
                        </g>
                    </svg>
                    <span className="spotify_audioplayer_spotify_listen_on">
                        Listen on
                    </span>
                </a>
            </div>
            <audio
                ref={audioPlayerRef}
                src={record.spotify_url}
                onTimeUpdate={() => {
                    const newProgress = formatProgressTime(
                        audioPlayerRef.current.currentTime - record.start
                    );
                    if (currentProgress !== newProgress) {
                        setCurrentProgress(newProgress);
                    }
                    if (audioPlayerRef.current.currentTime >= record.end) {
                        stopAudioPlayer();
                        onRecordFullyPlayed();
                    }
                }}
            ></audio>
        </>
    );
};

export default SpotifyAudioPlayer;
